import { Fragment, useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import {
  initNewMsalObject,
  signIn,
  isUserADoctor,
  isUserSignedIn,
  getAccountID,
  createUserDBAndAddToPatientGroup,
  hasTOSChecked,
  checkTOS,
  hasValidToken,
  signOut,
} from "../auth/auth";

//Material UI Imports
import { ThemeProvider, useMediaQuery } from "@mui/material";
import { theme } from "../utils/materialUI";

// Components
import NavBar from "./navigation/NavBar";
import Loading from "./common/Loading";
import HomePage from "../pages/HomePageResponsive";
import OnboardingPage from "../pages/OnboardingPage";
import SettingsPage from "../pages/AccountSettings/SettingsPage";
import TermsAndConditions from "./terms/termsandconditions";

import IndependentUserProfileDashboard from "../pages/DashboardPages/IndependentUserProfileDashboard";

// Lazy-loaded components for performance
import LazyDoctorHomeDiary from "../pages/HomeDiary/LazyDoctorHomeDiary";
import LazyStudyDashboard from "../pages/DashboardPages/LazyStudyDashboard";
import LazyPatientDirectory from "../pages/PatientDirectory/LazyPatientDirectory";
import LazyIndividualProfile from "../pages/DashboardPages/LazyIndividualProfile";

// React Icons
import { ReactComponent as HomeIcon } from "../icons/HomeIcon.svg";
import { ReactComponent as PatientDirectoryIcon } from "../icons/PatientDirectoryIcon.svg";
import { ReactComponent as StudyDashboardIcon } from "../icons/StudyDashboardIcon.svg";
import { ReactComponent as SettingsIcon } from "../icons/SettingsIcon.svg";

// CSS Imports
import "./App.css";
import MobileWarningModal from "./common/MobileWarningModal";
import IndependentUserStudies from "../pages/Studies/IndependentUserStudies";
import { TimezoneProvider } from "../utils/TimezoneContext";

// Custom TS Types
type NavRouteType = {
  path: string;
  exact: boolean;
  navIcon: JSX.Element;
  navText: string;
  main: JSX.Element;
}[];

// Instantiate MSAL
initNewMsalObject(false); //user not logging in

function App() {
  // ---- App State ----
  const [tosChecked, setTosChecked] = useState<boolean>(false);
  const [tosCheckComplete, setTosCheckComplete] = useState<boolean>(false);
  const [isDoc, setIsDoc] = useState<boolean | null>(null);
  const [isLoadingNavigation, setIsLoadingNavigation] = useState<boolean>(false);
  const [cachedDashboardData, setCachedDashboardData] = useState<{
    data: any;
    timestamp: number;
  } | null>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Doctor navigation routes for when the type of user is a doctor
  const [doctorNavRoutes, setDoctorNavRoutes] = useState<NavRouteType>([]);

  // Patient profile routes used
  const [patientProfileRoutes, setPatientProfileRoutes] =
    useState<NavRouteType>([]);

  // Account navigation route used when the type user
  const [accountRoute] = useState<NavRouteType>([]);

  // Messages navigation route used when the type user
  const [messageRoute] = useState<NavRouteType>([]);

  // Patient navigation routes for when the type of user is a patient
  const [patientNavRoutes, setPatientNavRoutes] = useState<NavRouteType>([]);

  const onAgreeTerms = async (): Promise<boolean> => {
    try {
      const tosAcceptanceRecorded = await checkTOS();
      if (tosAcceptanceRecorded) {
        setTosChecked(true);
        localStorage.setItem("termsAgreed", getAccountID() || "");
        return true;
      }
      console.error("Failed to record Terms of Service acceptance");
      return false;
    } catch (err) {
      console.error("Error during TOS acceptance:", err);
      return false;
    }
  };

  async function doesUserHaveValidSession() {
    let isSignedIn = isUserSignedIn();

    if (isSignedIn) {
      const tokenValid = hasValidToken();

      if (!tokenValid) {
        console.log("B2C token expired - signing out for HIPAA compliance");
        signOut();
        return false;
      }

      // Check if user has accepted TOS from the server 
      // And Wrap TOS check in try-catch-finally
      try {
        const hasAcceptedTOS = await hasTOSChecked();
        if (hasAcceptedTOS) {
          setTosChecked(true);
          localStorage.setItem("termsAgreed", getAccountID() || "");
        }
      } catch (error) {
        console.error("Failed to check TOS status:", error);
        // User can still proceed - we'll ask them to accept TOS if needed
      } finally {
        setTosCheckComplete(true);
      }

      let isDoctor = false;

      if (localStorage.getItem("isDoctor") === null) {
        isDoctor = await isUserADoctor();
        localStorage.setItem("isDoctor", isDoctor.toString());
      } else {
        isDoctor = localStorage.getItem("isDoctor") === "true";
      }

      await createUserDBAndAddToPatientGroup();

      if (isDoctor) {
        setIsDoc(true);
        populateDoctorNavRoutes();
      } else {
        populatePatientNavRoutes();
      }
    } else {
      // Clear any stale auth state when user is not signed in
      localStorage.removeItem("isDoctor");
      localStorage.removeItem("termsAgreed");
      setTosCheckComplete(true);

      if (
        window.localStorage["msal.error.description"] &&
        window.localStorage["msal.error.description"].includes("AADB2C90118")
      ) {
        delete window.localStorage["msal.error.description"];
        delete window.localStorage["isDoctor"];
        initNewMsalObject(true);
      }
    }
    return true;
  }

  // Immediately populate navigation routes with lazy-loaded components
  const populateDoctorNavRoutes = () => {
    try {
      setIsLoadingNavigation(true);

      // Callback to cache data when Home Diary loads
      const handleHomeDiaryDataLoaded = (data: any) => {
        setCachedDashboardData({
          data,
          timestamp: Date.now()
        });
      };

      // Set patient profile route
      setPatientProfileRoutes([
        {
          path: `/user/profile/:id`,
          exact: true,
          navIcon: (
            <PatientDirectoryIcon className="nav-icon icon-primary icon-secondary" />
          ),
          navText: "Patient Profile",
          main: <LazyIndividualProfile cachedData={cachedDashboardData || undefined} />,
        },
      ]);

      // Immediately set routes with lazy-loaded components
      const newDoctorNavRoutes: NavRouteType = [
        {
          path: "/",
          exact: true,
          navIcon: <HomeIcon className="nav-icon home-icon" />,
          navText: "Home Diary",
          main: <LazyDoctorHomeDiary cachedData={cachedDashboardData || undefined} onDataLoaded={handleHomeDiaryDataLoaded} />,
        },
        {
          path: "/dashboard",
          exact: true,
          navIcon: (
            <StudyDashboardIcon className="nav-icon icon-primary icon-secondary" />
          ),
          navText: "Study Dashboard",
          main: <LazyStudyDashboard cachedData={cachedDashboardData || undefined} />,
        },
        {
          path: "/patients",
          exact: true,
          navIcon: (
            <PatientDirectoryIcon className="nav-icon icon-primary icon-secondary" />
          ),
          navText: "Patient Directory",
          main: <LazyPatientDirectory cachedData={cachedDashboardData || undefined} />,
        },
        {
          path: "/settings",
          exact: true,
          navIcon: (
            <SettingsIcon className="nav-icon icon-primary icon-secondary" />
          ),
          navText: "Account Settings",
          main: <SettingsPage />,
        },
      ];

      setDoctorNavRoutes(newDoctorNavRoutes);
      setIsLoadingNavigation(false);
    } catch (error) {
      console.error("Error setting up navigation:", error);
      setIsLoadingNavigation(false);
    }
  };

  const populatePatientNavRoutes = () => {
    setPatientNavRoutes([
      {
        path: "/",
        exact: true,
        navIcon: (
          <StudyDashboardIcon className="nav-icon icon-primary icon-secondary" />
        ),
        navText: "myDashboard",
        main: <IndependentUserProfileDashboard />,
      },
      {
        path: "/studies",
        exact: true,
        navIcon: (
          <PatientDirectoryIcon className="nav-icon icon-primary icon-secondary" />
        ),
        navText: "Studies",
        main: <IndependentUserStudies />,
      },
      {
        path: "/settings",
        exact: true,
        navIcon: (
          <SettingsIcon className="nav-icon icon-primary icon-secondary" />
        ),
        navText: "Account Settings",
        main: <SettingsPage />,
      },
    ]);
  };

  // FIX: Suppress the exhaustive-deps warning - this is intentional
  // We only want this to run once on mount, not when functions change
  useEffect(() => {
    const validateUserSession = async () => {
      await doesUserHaveValidSession();
    };
    validateUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array is intentional - run once on mount

  // Stable callback to cache data when Home Diary loads
  const handleHomeDiaryDataLoaded = useCallback((data: any) => {
    setCachedDashboardData({
      data,
      timestamp: Date.now()
    });
  }, []);

  // Rebuild routes when cached data changes to pass updated data to lazy components
  useEffect(() => {
    if (isDoc && cachedDashboardData) {

      // Update patient profile route with new cached data
      setPatientProfileRoutes([
        {
          path: `/user/profile/:id`,
          exact: true,
          navIcon: (
            <PatientDirectoryIcon className="nav-icon icon-primary icon-secondary" />
          ),
          navText: "Patient Profile",
          main: <LazyIndividualProfile cachedData={cachedDashboardData || undefined} />,
        },
      ]);

      // Update doctor nav routes with new cached data (keep existing routes but update with new data)
      setDoctorNavRoutes((prevRoutes) =>
        prevRoutes.map((route) => {
          // Update all routes that use cached data
          if (route.path === "/") {
            return {
              ...route,
              main: <LazyDoctorHomeDiary cachedData={cachedDashboardData || undefined} onDataLoaded={handleHomeDiaryDataLoaded} />,
            };
          }
          if (route.path === "/dashboard") {
            return {
              ...route,
              main: <LazyStudyDashboard cachedData={cachedDashboardData || undefined} />,
            };
          }
          if (route.path === "/patients") {
            return {
              ...route,
              main: <LazyPatientDirectory cachedData={cachedDashboardData || undefined} />,
            };
          }
          return route;
        })
      );
    }
  }, [cachedDashboardData, isDoc, handleHomeDiaryDataLoaded]);

  // ---- Render JSX Elements ----

  // If signed in, go to the dashboard
  if (isUserSignedIn()) {
    if (!tosCheckComplete) {
      return (
        <div className="loadingContainer">
          <Loading />
        </div>
      );
    }

    if (!tosChecked) {
      return <TermsAndConditions onAgree={onAgreeTerms} />;
    }

    // Show loading while navigation is being set up
    if (isLoadingNavigation) {
      return (
        <div className="loadingContainer">
          <Loading />
        </div>
      );
    }

    const userNavRoutes: NavRouteType = [];
    if (isDoc) {
      userNavRoutes.push(
        ...doctorNavRoutes,
        ...patientProfileRoutes,
        ...messageRoute,
        ...accountRoute
        // ...defaultNavRoutes,
      );
    } else {
      userNavRoutes.push(
        ...patientNavRoutes,
        ...messageRoute,
        ...accountRoute
        // ...defaultNavRoutes
      );
    }

    return (
      <ThemeProvider theme={theme}>
        <TimezoneProvider>
          <>
            <Router>
            <div className="navbarDiv">
              {isDoc ? (
                <NavBar
                  routes={[...doctorNavRoutes, ...messageRoute, ...accountRoute]}
                />
              ) : (
                <NavBar
                  routes={[...patientNavRoutes, ...messageRoute, ...accountRoute]}
                />
              )}
            </div>
            <Routes>
              {userNavRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={route.main}
                />
              ))}
            </Routes>
          </Router>
          {isMobile ? <MobileWarningModal /> : null}
          </>
        </TimezoneProvider>
      </ThemeProvider>
    );
  }

  // If the user is not logged in, render the home routes
  const homeRoutes = [
    {
      path: "/",
      main: <HomePage signin={signIn} />,
    },
    {
      path: "/onboarding",
      main: <OnboardingPage signin={signIn} />,
    },
  ];

  return (
    <Fragment>
      <Router>
        <Routes>
          {homeRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.main} />
          ))}
        </Routes>
      </Router>
    </Fragment>
  );
}

export default App;
